import React from 'react';

import { styled } from '@this/constants/themes';
import A from '@this/components/shared/atoms/a';
import { Datepicker } from '@this/shared/ui/inputs/datepicker';
import { InLabel, FieldsWrapper, Field, ToggleField, InputArea, AddressArea, Select } from '../search/search';

import moment from '../../../lib/moment';
import SearchTravelersBlock from './search_travelers_block/search_travelers_block';
import SearchPeoplenumBlock from './search_peoplenum_block/search_peoplenum_block.template';
import SearchHotelOptionBlock from './search_hotel_option_block/search_hotel_option_block.template';
import type { SearchTemplateProps } from './types';
import AutoCompletableInput from '../autocompletable_input/autocompletable_input';

type Props = {
  getCurrentPlace: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  isLoadingCurrentPlace: boolean;
} & SearchTemplateProps;

class SearchRoundTrip extends React.Component<Props> {
  handlePlaceChange = (name: 'origin' | 'destination', value: string, address: string, placeId?: string) => {
    const hqi = this.hotelQueryItem();
    switch (name) {
      case 'origin':
        // 往路の出発地 = 復路の目的地
        this.outwardQueryItem().setOrigin(value, address, placeId);
        this.homewardQueryItem().setDestination(value, address, placeId);
        if (hqi) hqi.setOrigin(value, address, placeId);
        break;
      case 'destination': {
        // 往路の目的地 = ホテルの宿泊地 = 復路の出発地
        this.outwardQueryItem().setDestination(value, address, placeId);
        if (hqi) hqi.setDestination(value, address, placeId);
        this.homewardQueryItem().setOrigin(value, address, placeId);
        break;
      }
      default:
        throw new Error(`never: ${name}`);
    }
  };

  handleChangeDateTime = (name: 'outdate' | 'homedate', date: Date | null) => {
    if (!date) return;
    const dateMoment = moment(date);

    switch (name) {
      case 'outdate': {
        // 往路の出発日時を設定
        this.outwardQueryItem().setOutdate(dateMoment);
        this.outwardQueryItem().setOuthour(dateMoment.format('HH'));
        this.outwardQueryItem().setOutmin(dateMoment.format('mm'));

        // ホテルのチェックイン日を設定
        const hqi = this.hotelQueryItem();
        if (hqi) hqi.setOutdate(dateMoment);

        // 往路の出発日が復路の出発日よりも未来だったら強制的に往路出発日+1にする
        const homedateMoment = moment(this.homewardQueryItem().outdate);
        if (dateMoment >= homedateMoment) {
          this.homewardQueryItem().setOutdate(moment(dateMoment).add(1, 'day'));
        }
        break;
      }
      case 'homedate': {
        // 復路の出発日時を設定
        this.homewardQueryItem().setOutdate(dateMoment);
        this.homewardQueryItem().setOuthour(dateMoment.format('HH'));
        this.homewardQueryItem().setOutmin(dateMoment.format('mm'));

        // ホテルのチェックアウト日を設定
        const hqi = this.hotelQueryItem();
        if (hqi) hqi.setHomedate(dateMoment);
        break;
      }
      default:
        throw new Error(`never: ${name}`);
    }
  };

  handleChange =
    (name: 'outhour' | 'outmin' | 'outtype' | 'homehour' | 'homemin' | 'hometype') =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      switch (name) {
        case 'outhour':
          this.outwardQueryItem().setOuthour(value);
          break;
        case 'outmin':
          this.outwardQueryItem().setOutmin(value);
          break;
        case 'outtype':
          if (value !== 'arrival' && value !== 'departure') {
            throw new Error(`unexpected outtype: ${value}`);
          }
          this.outwardQueryItem().setOuttype(value);
          break;
        case 'homehour':
          this.homewardQueryItem().setOuthour(value);
          break;
        case 'homemin':
          this.homewardQueryItem().setOutmin(value);
          break;
        case 'hometype':
          if (value !== 'arrival' && value !== 'departure') {
            throw new Error(`unexpected outtype: ${value}`);
          }
          this.homewardQueryItem().setOuttype(value);
          break;
        default:
          throw new Error(`never: ${name}`);
      }
    };

  startDateCalendarInput = (kind: 'outdate' | 'homedate', ref_name: string, calendar_textbox_id: string) => () => {
    const transportDatePicker: any = this.refs[ref_name];
    switch (kind) {
      case 'outdate':
        transportDatePicker.initializeDate(this.outwardQueryItem().outdate);
        break;
      case 'homedate':
        transportDatePicker.initializeDate(this.homewardQueryItem().outdate);
        break;
      default:
        throw new Error(`never: ${name}`);
    }
    const element = document.getElementById(calendar_textbox_id);
    if (element) element.classList.add('selected-date-textbox');
  };

  private outwardQueryItem() {
    const items = this.props.query.getInputItems();
    return items[0];
  }

  private hotelQueryItem() {
    // 旅工房はホテルが無い
    const items = this.props.query.items;
    return items.find(i => i.itemType === 'hotel');
  }

  private homewardQueryItem() {
    const items = this.props.query.getInputItems();
    return items[items.length - 1];
  }

  private dateTimeValue(kind: 'outdate' | 'homedate') {
    const item = kind === 'outdate' ? this.outwardQueryItem() : this.homewardQueryItem();
    const date = moment(item.outdate);
    if (item.outhour) date.hour(parseInt(item.outhour, 10));
    if (item.outmin) date.minute(parseInt(item.outmin, 10));
    date.second(0).millisecond(0);
    return date.toDate();
  }

  render() {
    const { query, user, handleChange, getCurrentPlace, isLoadingCurrentPlace, availableOptions } = this.props;
    const restrictionDistanceAndTimeAvailability =
      availableOptions?.includes('restriction_distance_and_time') ?? false;
    const minDate = moment().startOf('minute').toDate();
    return (
      <div>
        <FieldsWrapper className="search__block__body__fields-wrapper">
          <Field>
            <InLabel htmlFor="origin">出発地</InLabel>
            <InputArea wrapped>
              <AutoCompletableInput
                onChange={this.handlePlaceChange}
                value={this.outwardQueryItem().origin}
                id="origin"
              />
              <div className="search__input-current-place">
                {isLoadingCurrentPlace ? (
                  <div className="search-loader-line-mask">
                    <SearchLoaderLine />
                  </div>
                ) : (
                  <CurrentPlace title="現在地を取得" onClick={getCurrentPlace}>
                    現在地
                  </CurrentPlace>
                )}
              </div>
            </InputArea>
            <AddressArea>
              <p>{this.outwardQueryItem().originAddress}</p>
              {restrictionDistanceAndTimeAvailability && user?.hasOrganizationBase() && (
                <p>{`拠点情報: ${user?.organizationBase.address}`}</p>
              )}
            </AddressArea>
          </Field>
        </FieldsWrapper>
        <FieldsWrapper className="search__block__body__fields-wrapper">
          <Field>
            <InLabel htmlFor="destination">目的地</InLabel>
            <InputArea wrapped>
              <AutoCompletableInput
                onChange={this.handlePlaceChange}
                value={this.outwardQueryItem().destination}
                id="destination"
              />
            </InputArea>
            <AddressArea>
              <p>{this.outwardQueryItem().destinationAddress}</p>
            </AddressArea>
          </Field>
        </FieldsWrapper>
        <FieldsWrapper className="search__block__body__fields-wrapper">
          <Field>
            <InLabel>往路</InLabel>
            <InputArea
              style={{ alignItems: 'center' }}
              wrapped
              id="simple_outdate_input"
              className="calendar-input-div"
            >
              <Datepicker
                type="datetime-local"
                value={this.dateTimeValue('outdate')}
                dateFormat="YYYY/MM/DD (ddd) HH:mm"
                onChange={date => this.handleChangeDateTime('outdate', date)}
                min={minDate}
                disabledClear
                variant="ghost"
              />
              <div className="search__select-wrapper" style={{ marginLeft: '10px' }}>
                <Select
                  name="outtype"
                  value={this.outwardQueryItem().outtype}
                  onChange={this.handleChange('outtype')}
                >
                  <option value="departure">出発</option>
                  <option value="arrival">到着</option>
                </Select>
              </div>
            </InputArea>
          </Field>
        </FieldsWrapper>
        <FieldsWrapper className="search__block__body__fields-wrapper">
          <ToggleField disabled={false}>
            <InLabel>復路</InLabel>
            <InputArea
              style={{ alignItems: 'center' }}
              wrapped
              id="simple_homedate_input"
              className="calendar-input-div"
            >
              <Datepicker
                type="datetime-local"
                value={this.dateTimeValue('homedate')}
                dateFormat="YYYY/MM/DD (ddd) HH:mm"
                onChange={date => this.handleChangeDateTime('homedate', date)}
                min={minDate}
                disabledClear
                variant="ghost"
              />
              <div className="search__select-wrapper" style={{ marginLeft: '10px' }}>
                <Select
                  name="hometype"
                  value={this.homewardQueryItem().outtype}
                  onChange={this.handleChange('hometype')}
                >
                  <option value="departure">出発</option>
                  <option value="arrival">到着</option>
                </Select>
              </div>
            </InputArea>
          </ToggleField>
        </FieldsWrapper>
        {user ? (
          <SearchTravelersBlock query={query} user={user} handleChange={handleChange} />
        ) : (
          <SearchPeoplenumBlock query={query} handleChange={handleChange} />
        )}
        <SearchHotelOptionBlock query={query} handleChange={handleChange} />
      </div>
    );
  }
}

const SearchLoaderLine = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  box-shadow: inset 0 1px 1px 3px ${props => props.theme.linkColor};
`;

const CurrentPlace = styled(A)`
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
`;

export default SearchRoundTrip;
